import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import type { Schedule, ScheduleType, Member } from '@tsuyosa/types';
import { useAuth } from '../context/AuthContext';
import {
  getAdminScheduleById,
  createSchedule,
  updateSchedule,
  getAdminScheduleTypes,
  getAdminMembers,
  getScheduleCheki,
  updateChekiPricing,
  type ChekiPricingRow,
} from '../lib/api';
import { Spinner, Button } from '@tsuyosa/ui';

type ScheduleStatus = 'upcoming' | 'sold_out' | 'cancelled';

const DEFAULT_FORM: Partial<Schedule> = {
  title:          '',
  date:           new Date().toISOString().split('T')[0],
  start_time:     '',
  end_time:       '',
  venue:          '',
  type_id:        '',
  status:         'upcoming',
  sales_start_at: '',
  sales_end_at:   '',
  is_active:      true,
  cheki_enabled:  false,
};

const DEFAULT_PRICING: ChekiPricingRow[] = [
  { type: '2shot_solo',  price_preorder: 0, price_onspot: 0 },
  { type: 'all_member',  price_preorder: 0, price_onspot: 0 },
];

export default function ScheduleFormPage() {
  const { id }  = useParams<{ id?: string }>();
  const isEdit  = Boolean(id);
  const navigate  = useNavigate();
  const { accessToken } = useAuth();

  const [form, setForm]                     = useState<Partial<Schedule>>(DEFAULT_FORM);
  const [types, setTypes]                   = useState<ScheduleType[]>([]);
  const [members, setMembers]               = useState<Member[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [pricing, setPricing]               = useState<ChekiPricingRow[]>(DEFAULT_PRICING);
  const [isSavingPricing, setIsSavingPricing] = useState(false);
  const [pricingError, setPricingError]     = useState('');
  const [pricingSuccess, setPricingSuccess] = useState(false);
  const [isLoading, setIsLoading]           = useState(false);
  const [isFetching, setIsFetching]         = useState(isEdit);
  const [error, setError]                   = useState('');

  useEffect(() => {
    if (!accessToken) return;

    Promise.all([
      getAdminScheduleTypes(accessToken),
      getAdminMembers(accessToken).then((r) => r.data.filter((m) => m.activity_status === 'active')),
    ]).then(([t, m]) => {
      setTypes(t);
      setMembers(m);
    });

    if (!isEdit || !id) { setIsFetching(false); return; }
    Promise.all([
      getAdminScheduleById(accessToken, id),
      getScheduleCheki(id).catch(() => ({ cheki_enabled: false, pricing: [] })),
    ])
      .then(([found, cheki]) => {
        setForm({
          ...found,
          sales_start_at: found.sales_start_at ? found.sales_start_at.slice(0, 16) : '',
          sales_end_at:   found.sales_end_at   ? found.sales_end_at.slice(0, 16)   : '',
        });
        if (found.members) {
          setSelectedMembers(found.members.map((m) => m.member_id));
        }
        if (cheki.pricing.length > 0) {
          setPricing(cheki.pricing.map(p => ({
            type: p.type,
            price_preorder: p.price_preorder,
            price_onspot: p.price_onspot,
          })));
        }
      })
      .finally(() => setIsFetching(false));
  }, [isEdit, accessToken, id]);

  function toggleMember(memberId: string) {
    setSelectedMembers((prev) =>
      prev.includes(memberId) ? prev.filter((m) => m !== memberId) : [...prev, memberId]
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!accessToken) return;
    setError('');
    setIsLoading(true);
    try {
      const payload = {
        ...form,
        sales_start_at: form.sales_start_at ? new Date(form.sales_start_at).toISOString() : '',
        sales_end_at:   form.sales_end_at   ? new Date(form.sales_end_at).toISOString()   : '',
        members: selectedMembers.map((member_id, sort_order) => ({ member_id, sort_order })),
      };
      if (isEdit && id) {
        await updateSchedule(accessToken, id, payload);
      } else {
        await createSchedule(accessToken, payload);
      }
      navigate('/schedule');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  }

  function updatePricingRow(type: '2shot_solo' | 'all_member', field: 'price_preorder' | 'price_onspot', value: number) {
    setPricing(prev => prev.map(r => r.type === type ? { ...r, [field]: value } : r));
  }

  async function handleSavePricing() {
    if (!accessToken || !id) return;
    setPricingError('');
    setPricingSuccess(false);
    setIsSavingPricing(true);
    try {
      await updateChekiPricing(accessToken, id, pricing);
      setPricingSuccess(true);
      setTimeout(() => setPricingSuccess(false), 3000);
    } catch (err) {
      setPricingError(err instanceof Error ? err.message : 'Gagal menyimpan harga');
    } finally {
      setIsSavingPricing(false);
    }
  }

  if (isFetching) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/schedule')}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-white/5 hover:text-gray-200"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-gray-100">
          {isEdit ? 'Edit Jadwal' : 'Tambah Jadwal'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Judul */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">
            Judul <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            required
            value={form.title ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Tanggal */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-400">
              Tanggal <span className="text-red-400">*</span>
            </label>
            <input
              type="date"
              required
              value={form.date ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none [color-scheme:dark]"
            />
          </div>
          {/* Tipe Event */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Tipe Event</label>
            <select
              value={form.type_id ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, type_id: e.target.value }))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none"
            >
              <option value="">— Pilih tipe —</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name_id}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Jam Mulai */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Jam Mulai</label>
            <input
              type="time"
              value={form.start_time ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, start_time: e.target.value }))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none [color-scheme:dark]"
            />
          </div>
          {/* Jam Selesai */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Jam Selesai</label>
            <input
              type="time"
              value={form.end_time ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, end_time: e.target.value }))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none [color-scheme:dark]"
            />
          </div>
        </div>

        {/* Venue */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">Venue</label>
          <input
            type="text"
            value={form.venue ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, venue: e.target.value }))}
            placeholder="Nama gedung / lokasi"
            className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none"
          />
        </div>

        {/* Status */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">Status</label>
          <select
            value={form.status ?? 'upcoming'}
            onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as ScheduleStatus }))}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none"
          >
            <option value="upcoming">Upcoming</option>
            <option value="sold_out">Sold Out</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Sales dibuka */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Penjualan Dibuka</label>
            <input
              type="datetime-local"
              value={form.sales_start_at?.slice(0, 16) ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, sales_start_at: e.target.value }))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none [color-scheme:dark]"
            />
          </div>
          {/* Sales ditutup */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Penjualan Ditutup</label>
            <input
              type="datetime-local"
              value={form.sales_end_at?.slice(0, 16) ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, sales_end_at: e.target.value }))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none [color-scheme:dark]"
            />
          </div>
        </div>

        {/* Lineup member */}
        {members.length > 0 && (
          <div>
            <label className="mb-2 block text-xs font-medium text-gray-400">Lineup Member</label>
            <div className="flex flex-wrap gap-2">
              {members.map((m) => (
                <label
                  key={m.id}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 hover:border-pink-500/30 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(m.id)}
                    onChange={() => toggleMember(m.id)}
                    className="h-3.5 w-3.5 rounded accent-pink-500"
                  />
                  <span className="text-xs text-gray-300">{m.nickname}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Tampilkan di publik */}
        <label className="flex cursor-pointer items-center gap-2.5">
          <input
            type="checkbox"
            checked={form.is_active ?? true}
            onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
            className="h-4 w-4 rounded accent-pink-500"
          />
          <span className="text-sm text-gray-300">Tampilkan di halaman publik</span>
        </label>

        {/* Aktifkan Cheki Pre-Order */}
        <label className="flex cursor-pointer items-center gap-2.5">
          <input
            type="checkbox"
            checked={form.cheki_enabled ?? false}
            onChange={(e) => setForm((p) => ({ ...p, cheki_enabled: e.target.checked }))}
            className="h-4 w-4 rounded accent-pink-500"
          />
          <span className="text-sm text-gray-300">Aktifkan Cheki Pre-Order</span>
        </label>

        {error && (
          <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={() => navigate('/schedule')}>
            Cancel
          </Button>
          <Button type="submit" variant="tsuyosa" size="sm" isLoading={isLoading}>
            {isEdit ? 'Simpan Perubahan' : 'Tambah Jadwal'}
          </Button>
        </div>
      </form>

      {/* Cheki Pricing — only in edit mode */}
      {isEdit && (
        <div className="mt-10 border-t border-[var(--color-border)] pt-8">
          <h2 className="mb-4 text-base font-semibold text-gray-100">Harga Cheki</h2>
          <div className="space-y-4">
            {pricing.map((row) => (
              <div key={row.type} className="rounded-lg border border-[var(--color-border)] p-4">
                <p className="mb-3 text-xs font-medium text-gray-400 uppercase">
                  {row.type === '2shot_solo' ? '2 Shot Solo' : 'All Member'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">Harga Pre-Order (Rp)</label>
                    <input
                      type="number"
                      min={0}
                      value={row.price_preorder}
                      onChange={(e) => updatePricingRow(row.type, 'price_preorder', Number(e.target.value))}
                      className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">Harga On-Spot (Rp)</label>
                    <input
                      type="number"
                      min={0}
                      value={row.price_onspot}
                      onChange={(e) => updatePricingRow(row.type, 'price_onspot', Number(e.target.value))}
                      className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {pricingError && (
            <p className="mt-3 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{pricingError}</p>
          )}
          {pricingSuccess && (
            <p className="mt-3 rounded-lg bg-green-500/10 px-3 py-2 text-xs text-green-400">Harga berhasil disimpan.</p>
          )}

          <div className="mt-4">
            <Button
              type="button"
              variant="tsuyosa"
              size="sm"
              isLoading={isSavingPricing}
              onClick={handleSavePricing}
            >
              Simpan Harga Cheki
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
